
/* eslint-disable no-empty */
import { PERMISSIONS } from '@/constants/permissions';
import { checkAllowanceLimit } from '@/services/affiliates.service';
import {
  getFile,
  getFilePayments,
  getFileAttachemnts,
  hasPendingDocuments,
  getFileTicketAttachemnts,
  getFileReceiptAttachemnts,
  getFileVoucherAttachemnts,
  getVoucherTypes,
  getAffiliateFile,
  getTramitTypes,
  getReceiptTypes,
  getDocumentTypes,
  getFileOdontograms,
  getFileErrors,
  getDispositions,
  getAuthorization,
  uploadDisosition,
  getFilesPrivateAttachments,
  getFilesValidations,
  getCoveragesPerFile,
} from '@/services/files.service';
import { hasPermission } from '@/services/login.service';
import { notification } from 'antd';

import { redirectNotFound } from '@/utils/errorRedirect';

const Model = {
  namespace: 'tramitDetails',
  state: {
    file: null,
    tickets: [],
    receipts: [],
    voucherTypes: [],
    vouchers: [],
    payments: [],
    fileErrors: [],
    attachments: [],
    odontograms: [],
    tramitTypes: [],
    receiptTypes: [],
    documentTypes: [],
    fileValidations: [],
    filePrivateAttachments: [],
    filescopes: [],
    authorizations: [],
    isLoadingFiles: true,
    pendingDocuments: false,
  },

  effects: {
    *fetchFile({ payload }, { call, put }) {
      try {
        let response;
        let limit;
        let pendingDocuments = false;

        if (payload.affiliate) {
          response = yield call(getAffiliateFile, payload.affiliate, payload.id);
        } else {
          response = yield call(getFile, payload.id);
          pendingDocuments = yield call(hasPendingDocuments, payload.id);

          try {
            limit = yield call(checkAllowanceLimit, response.data.affiliate.id, {
              file: payload.data.id,
              period: response.data.period,
              allowance_id: response.data.allowance.id,
              allowance_amount: response.data.allowance_amount,
              amount_requested: response.data.amount_awarded
                ? Number(response.data.amount_awarded || 0) + response.data.pharmacy_amount
                : Number(response.data.amount_requested || 0) + response.data.pharmacy_amount,
            });
          } catch (error) {
            limit = { result: false, message: error?.data?.message || null };
          }
          if (response.data.tramit_type.key === 'EXCEPTION') limit.result = true;
        }
        yield put({
          type: 'show',
          payload: { file: response.data, limit, pendingDocuments},
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *addOdontogram({ payload }, { put }) {
      yield put({
        type: 'show',
        payload: { odontograms: [payload] },
      });
    },

    *fetchFileTypes(_, { call, put }) {
      try {
        const response = yield call(getTramitTypes);
        yield put({
          type: 'show',
          payload: { tramitTypes: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchVouchersAttachments(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ tramitDetails }) => tramitDetails.file);
        const affiliate = selectedFile.affiliate.id;
        const vouchers = yield call(getFileVoucherAttachemnts, affiliate, selectedFile.id);
        yield put({
          type: 'show',
          payload: { vouchers: vouchers.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchReceiptsAttachments(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ tramitDetails }) => tramitDetails.file);
        const affiliate = selectedFile.affiliate.id;
        const receipts = yield call(getFileReceiptAttachemnts, affiliate, selectedFile.id);
        yield put({
          type: 'show',
          payload: { receipts: receipts.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchTicketsAttachments(_, { call, put, select }) {
      try {
        const selectedFile = yield select(({ tramitDetails }) => tramitDetails.file);
        const affiliate = selectedFile.affiliate.id;

        const tickets = yield call(getFileTicketAttachemnts, affiliate, selectedFile.id);
        yield put({
          type: 'show',
          payload: { tickets: tickets.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

    *fetchFileErrors(_, { call, put }) {
      const fileErrors = yield call(getFileErrors);
      yield put({
        type: 'show',
        payload: { fileErrors: fileErrors.data },
      });
    },

    *fetchReceiptTypes(_, { all, call, put }) {
      try {
        const [receiptTypes, documentTypes] = yield all([
          call(getReceiptTypes),
          call(getDocumentTypes),
        ]);
        yield put({
          type: 'show',
          payload: { receiptTypes: receiptTypes.data, documentTypes: documentTypes.data },
        });
      } catch (error) {
        notification.error({ message: error?.response?.data?.detail ?? 'Ocurrió un error al cargar los datos de esta pantalla.' });
      }
    },

    *fetchVoucherTypes(_, { all, call, put }) {
      try {
        const [voucherTypes] = yield all([call(getVoucherTypes)]);
        yield put({
          type: 'show',
          payload: { voucherTypes: voucherTypes.data },
        });
      } catch (error) {
        notification.error({ message: error?.response?.data?.detail ?? 'Ocurrió un error al cargar los datos de esta pantalla.' });
      }
    },

    *fetchFilesAttachments({ payload }, { call, put, all, select }) {
      yield put({ type: 'show', payload: { isLoadingFiles: true } });

      const { file } = payload;
      const selectedFile = yield select(({ tramitDetails }) => tramitDetails.file);
      const affiliate = selectedFile.affiliate.id;
      if (selectedFile.odontrogram !== null) {
        try {
          const [attachments, tickets, receipts, vouchers, odontograms, filescopes, authorizations] = yield all([
            call(getFileAttachemnts, affiliate, file),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.TICKETS.VIEW)
                ? getFileTicketAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.RECEIPTS.VIEW)
                ? getFileReceiptAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.VOUCHERS.VIEW)
                ? getFileVoucherAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.ODONTOGRAMS.VIEW)
                ? getFileOdontograms
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit
                ? getDispositions
                : () => Promise.resolve([]),
              file,
            ),
            call(getAuthorization, file),
          ]);

          yield put({
            type: 'show',
            payload: { attachments: attachments.data, tickets: tickets.data, receipts: receipts.data, vouchers: vouchers.data, odontograms: odontograms.data, filescopes: filescopes.data.results, authorizations: authorizations.data.results },
          });
        } catch (error) {
          redirectNotFound(error);
        }
      } else {
        try {
          const [attachments, tickets, receipts, vouchers, filescopes, authorizations] = yield all([
            call(getFileAttachemnts, affiliate, file),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.TICKETS.VIEW)
                ? getFileTicketAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.RECEIPTS.VIEW)
                ? getFileReceiptAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit || hasPermission(PERMISSIONS.VOUCHERS.VIEW)
                ? getFileVoucherAttachemnts
                : () => Promise.resolve([]),
              affiliate,
              file,
            ),
            call(
              selectedFile.can_edit
                ? getDispositions
                : () => Promise.resolve([]),
              file,
            ),
            call(getAuthorization, file),
          ]);

          yield put({
            type: 'show',
            payload: { attachments: attachments.data, tickets: tickets.data, receipts: receipts.data, vouchers: vouchers.data, filescopes: filescopes.data.results, authorizations: authorizations.data.results },
          });
        } catch (error) {}
      }
      yield put({ type: 'show', payload: { isLoadingFiles: false } });
    },

    *fetchFilePrivateAttachments({ payload }, { call, put, all }) {
      yield put({ type: 'show', payload: { isLoadingFiles: true } });
      const { file } = payload;

      try {
        const [filePrivateAttachments] = yield all([call(getFilesPrivateAttachments, file)]);
        yield put({
          type: 'show',
          payload: { filePrivateAttachments: filePrivateAttachments.data },
        });
      } catch (error) {}
      yield put({ type: 'show', payload: { isLoadingFiles: false } });
    },

    *fetchFilesValidations({ payload }, { call, put, all }) {
      yield put({ type: 'show', payload: { isLoadingFiles: true } });
      const { file } = payload;

      try {
        const [fileValidations] = yield all([call(getFilesValidations, file)]);
        yield put({
          type: 'show',
          payload: { fileValidations: fileValidations.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
      yield put({ type: 'show', payload: { isLoadingFiles: false } });
    },

    *fetchFilePayments({ payload }, { call, put }) {
      try {
        const response = yield call(getFilePayments, payload);

        yield put({
          type: 'show',
          payload: { payments: response.data },
        });
      } catch (error) {
        redirectNotFound(error);
      }
    },

  },

  reducers: {
    show(state, { payload }) {
      return { ...state, ...payload };
    },
  },
};

export default Model;
